import ProTag from "@/app/components/ProTag";
import { Box, Typography } from "@mui/material";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";

/**
 * PartnerPerspectivesSection
 * 水平分页且可左右拖拽/滑动的合作方研究报告列表组件。
 *
 * 主要特性：
 * - 自适应分页：小屏幕每行 1 个、每页 6 行；大屏幕每行 3 个、每页 3 行。
 * - 动态高度动画：容器高度随当前页内容变化平滑过渡。
 * - 指针统一拖拽：使用 Pointer Events 同时支持鼠标 / 触摸 / 手写笔（不再单独绑定 touch* 事件）。
 * - 吸附阈值：水平位移超过容器宽度的 15% 切换页面，否则回弹。
 * - 连续指示器：底部只有一条轨道，滑块表示当前页，拖拽过程中滑块实时跟随。
 * - 点击兜底：桌面端在开启 pointer capture 时，极短且位移很小的拖动视为点击，避免丢失原生 click。
 * - 可访问性：报告项可聚焦，Enter / 空格键触发打开。
 */

type PartnerPerspectivesSectionProps = {
  partnerReports: any[];
  onReportClick: (url: string) => void;
};

export default function PartnerPerspectivesSection({ partnerReports, onReportClick }: PartnerPerspectivesSectionProps) {
  const t = useTranslations("Research");
  const [currentPage, setCurrentPage] = useState(0);
  const [contentHeight, setContentHeight] = useState("auto");
  const trackRef = useRef<HTMLDivElement | null>(null);
  const pageRefs = useRef<Array<HTMLDivElement | null>>([]);
  // 不再使用 pointer capture，改为 window 级监听 + 拖拽阈值
  const pointerIdRef = useRef<number | null>(null);
  const dragActiveRef = useRef(false); // 真实进入拖拽（超过阈值）标记
  const DRAG_START_THRESHOLD = 5; // 像素：超过后进入拖拽
  const dragStartX = useRef(0);
  const dragStartTime = useRef(0); // ms 时间戳，用于计算滑动速度
  const containerWidth = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0); // px
  // 解决 pointerup 闭包中 dragOffset 可能为旧值的问题：增加 ref 始终存储最新偏移
  const dragOffsetRef = useRef(0);

  const itemsPerRow = typeof window !== "undefined" && window.innerWidth <= 900 ? 1 : 3;
  // 小屏幕每页 6 行（需求变更）；大屏幕每页 3 行。
  const rowsPerPage = typeof window !== "undefined" && window.innerWidth <= 900 ? 6 : 3;
  const itemsPerPage = itemsPerRow * rowsPerPage;
  const pages = Math.ceil(partnerReports.length / itemsPerPage);

  const showPageItems = (page: number) => {
    const startIdx = page * itemsPerPage;
    const items = partnerReports.slice(startIdx, startIdx + itemsPerPage);
    const pageRows: any[][] = [];
    for (let i = 0; i < items.length; i += itemsPerRow) {
      pageRows.push(items.slice(i, i + itemsPerRow));
    }
    return pageRows;
  };

  useEffect(() => {
    const handleResize = () => {
      setCurrentPage(0);
      if (trackRef.current) {
        containerWidth.current = trackRef.current.clientWidth;
      }
      if (pageRefs.current[currentPage]) {
        setContentHeight(`${pageRefs.current[currentPage]!.scrollHeight}px`);
      }
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (pages > 0 && currentPage >= pages) {
      setCurrentPage(0);
    }
  }, [pages, currentPage]);

  useEffect(() => {
    // 页码或数据变化时更新容器宽度与高度
    if (trackRef.current) {
      containerWidth.current = trackRef.current.clientWidth;
    }
    const el = pageRefs.current[currentPage];
    if (el) {
      setContentHeight(`${el.scrollHeight}px`);
    } else if (trackRef.current) {
      setContentHeight(`${trackRef.current.scrollHeight}px`);
    }
  }, [currentPage, partnerReports, itemsPerPage]);

  const handlePageChange = (page: number) => {
    const next = Math.max(0, Math.min(page, pages - 1));
    setCurrentPage(next);
    // 重置拖拽状态
    setDragOffset(0);
    setIsDragging(false);
    setTimeout(() => {
      const el = pageRefs.current[next];
      if (el) setContentHeight(`${el.scrollHeight}px`);
    }, 60);
  };

  // 指针事件：阈值触发拖拽（>5px）+ window 监听，避免影响点击
  const cleanupPointerListeners = () => {
    window.removeEventListener("pointermove", handleWindowPointerMove);
    window.removeEventListener("pointerup", handleWindowPointerUp);
    window.removeEventListener("pointercancel", handleWindowPointerCancel);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if ((e as any).button && (e as any).button !== 0) return; // 仅主键
    pointerIdRef.current = e.pointerId;
    dragStartX.current = e.clientX;
    dragStartTime.current = performance.now();
    dragActiveRef.current = false;
    setDragOffset(0);
    setIsDragging(false);
    window.addEventListener("pointermove", handleWindowPointerMove, { passive: true });
    window.addEventListener("pointerup", handleWindowPointerUp, { passive: true });
    window.addEventListener("pointercancel", handleWindowPointerCancel, { passive: true });
  };

  const handleWindowPointerMove = (e: PointerEvent) => {
    if (pointerIdRef.current == null || e.pointerId !== pointerIdRef.current) return;
    const delta = e.clientX - dragStartX.current;
    const absDelta = Math.abs(delta);
    if (!dragActiveRef.current) {
      if (absDelta >= DRAG_START_THRESHOLD) {
        dragActiveRef.current = true;
        setIsDragging(true);
      } else {
        return; // 未超过阈值，不进入拖拽
      }
    }
    // 同步更新 state + ref；state 触发渲染，ref 确保 pointerup 读取到最新值（避免闭包旧值导致 delta=0）
    setDragOffset(delta);
    dragOffsetRef.current = delta;
  };

  const finalizePointerGesture = (e: PointerEvent) => {
    if (pointerIdRef.current == null || e.pointerId !== pointerIdRef.current) return;
    // 使用 ref 中的最新偏移值，避免由于 React state 异步或闭包导致读取到过期 0
    const delta = dragOffsetRef.current;
    const absDelta = Math.abs(delta);
    const w = containerWidth.current || (trackRef.current ? trackRef.current.clientWidth : 0);
    const PAGE_THRESHOLD_RATIO = 0.08; // 降低翻页阈值：原 15% -> 8%
    const pageThreshold = w * PAGE_THRESHOLD_RATIO;
    const duration = performance.now() - dragStartTime.current;
    const velocity = duration > 0 ? absDelta / duration : 0; // px/ms
    const FLICK_VELOCITY = 0.6; // 轻扫速度阈值：约 0.6px/ms (~600px/s)
    const MIN_FLICK_DISTANCE = 30; // 轻扫最小位移

    const shouldFlipByDistance = dragActiveRef.current && absDelta > pageThreshold;
    const shouldFlipByFlick = dragActiveRef.current && absDelta >= MIN_FLICK_DISTANCE && velocity >= FLICK_VELOCITY;
    if (shouldFlipByDistance || shouldFlipByFlick) {
      handlePageChange(delta > 0 ? currentPage - 1 : currentPage + 1);
    } else {
      // 拖拽但未超过翻页阈值 -> 回弹；若轻微且快速也当点击
      const CLICK_TOLERANCE = 8;
      if (absDelta <= CLICK_TOLERANCE && duration < 200) {
        const targetEl = (e.target as HTMLElement).closest<HTMLElement>("[data-report-item]");
        if (targetEl) {
          const url = targetEl.getAttribute("data-url");
          if (url) {
            onReportClick(url);
          }
        }
      }
    }

    // 清理状态
    pointerIdRef.current = null;
    dragActiveRef.current = false;
    setIsDragging(false);
    setDragOffset(0);
    dragOffsetRef.current = 0;
    cleanupPointerListeners();
  };

  const handleWindowPointerUp = (e: PointerEvent) => finalizePointerGesture(e);
  const handleWindowPointerCancel = (e: PointerEvent) => {
    if (pointerIdRef.current == null || e.pointerId !== pointerIdRef.current) return;
    pointerIdRef.current = null;
    dragActiveRef.current = false;
    setIsDragging(false);
    setDragOffset(0);
    cleanupPointerListeners();
  };

  if (!partnerReports.length) {
    return null;
  }

  // 计算滑块（页指示器）宽度与左侧偏移百分比
  const knobWidthPct = pages > 0 ? 100 / pages : 0;
  // progressWithinPage：拖拽向下一页时为正（dragOffset 为负）
  const progressWithinPage = containerWidth.current ? -dragOffset / containerWidth.current : 0;
  const rawLeftPct = pages > 0 ? ((currentPage + progressWithinPage) / pages) * 100 : 0;
  const knobLeftPct = Math.max(0, Math.min(rawLeftPct, 100 - knobWidthPct));

  // 计算内部轨道的百分比平移（避免首帧宽度为 0 时 px 计算不稳定）
  const pageWidthPct = pages > 0 ? 100 / pages : 100;
  const baseTranslatePct = -(currentPage * pageWidthPct);
  const dragPct = containerWidth.current ? (dragOffset / containerWidth.current) * pageWidthPct : 0;
  const translatePct = baseTranslatePct + dragPct;

  return (
    <Box sx={{ mt: "6rem", mb: 5.5 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 5, md: 8 },
          alignItems: { xs: "center", md: "center" },
          justifyContent: { xs: "center", md: "space-between" },
          mb: 6,
        }}>
        {/* 图片在左侧 */}
        <Box
          component="img"
          src="/images/research-partner-perspectives.png"
          sx={{
            width: { xs: "100%", sm: "80%", md: "50%" },
            height: "auto",
            objectFit: "cover",
            borderRadius: 1.5,
            order: { xs: 0, md: 1 }, // 小屏幕下放在后面，大屏幕左侧
          }}
        />
        {/* 标题和描述在右侧 */}
        <Box
          sx={{
            maxWidth: { xs: "100%", md: "30rem" },
            display: "flex",
            flexDirection: "column",
            alignItems: { xs: "center", md: "flex-start" }, // 大屏幕右侧左对齐
            justifyContent: "center",
            order: { xs: 1, md: 0 }, // 小屏幕下在前面，大屏幕右侧
            textAlign: { xs: "center", md: "left" }, // 大屏幕左对齐
            px: { xs: 2, md: 0 },
          }}>
          <Typography
            variant="h4"
            sx={{
              fontSize: "var(--font-size-3xl)",
              fontWeight: 600,
              mb: 1.5,
            }}>
            {t("partnerPerspectives.title")}
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary", fontSize: "1rem" }}>
            {t("partnerPerspectives.description")}
          </Typography>
        </Box>
      </Box>
      <Box
        ref={trackRef}
        onPointerDown={onPointerDown}
        sx={{
          width: "100%",
          mb: 3.5,
          height: contentHeight,
          transition: "height 0.3s ease",
          overflow: "hidden",
          position: "relative",
          // allow horizontal pan gestures
          touchAction: "pan-y",
          WebkitUserSelect: "none",
          userSelect: "none",
        }}>
        {/* 轨道：包含各分页内容 */}
        <Box
          sx={{
            display: "flex",
            width: `${pages * 100}%`,
            height: "100%",
          }}
          style={{
            transform: `translateX(${translatePct}%)`,
            transition: isDragging ? "none" : "transform 0.35s cubic-bezier(.2,.8,.2,1)",
          }}>
          {Array.from({ length: pages }).map((_, pIdx) => {
            const pageRows = showPageItems(pIdx);
            return (
              <Box
                key={pIdx}
                ref={(el) => {
                  pageRefs.current[pIdx] = el as HTMLDivElement | null;
                }}
                sx={{
                  width: `${100 / pages}%`,
                  flex: `0 0 ${100 / pages}%`,
                  boxSizing: "border-box",
                  px: { xs: 0, md: 0 },
                }}>
                {pageRows.map((row, rowIdx) => (
                  <Box
                    key={rowIdx}
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      justifyContent: "space-between",
                      mb: 3,
                      gap: { xs: 2, md: 5 },
                    }}>
                    {row.map((report, colIdx) => (
                      <Box
                        key={colIdx}
                        data-report-item
                        data-url={report.url}
                        role="button"
                        tabIndex={0}
                        sx={{
                          flex: "1 1 0",
                          py: 1.5,
                          borderBottom: "1px solid",
                          borderColor: "divider",
                          mx: { xs: 0, md: colIdx === 1 ? 4 : 0 },
                          display: "flex",
                          alignItems: "flex-end",
                          width: "100%",
                          outline: "none",
                          "&:focus-visible": {
                            boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}`,
                          },
                        }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            color: "text.primary",
                            whiteSpace: "normal",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            cursor: "pointer",
                            transition: "color 0.2s",
                            "&:hover": { color: "primary.main" },
                            width: "100%",
                          }}>
                          {report.isFree || (
                            <ProTag sx={{ display: "inline-block", mr: 1, position: "relative", top: "-2px" }} />
                          )}
                          {report.title}
                        </Typography>
                      </Box>
                    ))}
                    {itemsPerRow > 1 &&
                      Array.from({ length: Math.max(0, itemsPerRow - row.length) }).map((_, i) => (
                        <Box
                          key={`empty-${i}`}
                          sx={{
                            flex: "1 1 0",
                            px: 2,
                            py: 2,
                            display: { xs: "none", md: "block" },
                          }}
                        />
                      ))}
                  </Box>
                ))}
              </Box>
            );
          })}
        </Box>
      </Box>
      {/* 连续滑块指示器（无间隙） */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <Box
          onClick={(e: any) => {
            // 点击轨道任意位置：根据点击比例跳转到对应页
            const rect = e.currentTarget.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const w = rect.width || 1;
            const pct = x / w;
            const idx = Math.floor(pct * pages);
            handlePageChange(idx);
          }}
          sx={{
            width: { xs: "90%", md: "60%" },
            height: "0.25rem",
            borderRadius: 1,
            bgcolor: "background.paper",
            px: 0.5,
            cursor: "pointer",
            position: "relative",
            overflow: "hidden",
          }}>
          {/* rail */}
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "100%",
              bgcolor: "text.tertiary",
              opacity: 0.6,
            }}
          />
          {/* moving knob */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              bottom: 0,
              borderRadius: 1,
              bgcolor: "text.primary",
              transition: isDragging ? "none" : "left 0.25s ease",
            }}
            style={{
              left: `${knobLeftPct}%`,
              width: `${knobWidthPct}%`,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
